import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Menu,
  MenuItem,
} from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Search from '@material-ui/icons/Search';
import React, { SyntheticEvent, useEffect, useState } from 'react';
import create, { UseStore } from 'zustand';
import { Text } from '../../core/controls';
import { Alert } from '../../core/domain';
import {
  Expansion,
  ExpansionDetails,
  ExpansionSummary,
} from '../../core/expansion';
import { formApi } from '../../core/form';
import { IcdCptApi } from '../../core/icd-cpt-api';
import { orange } from '../../core/theme';
import { medicalEditsRequest, truCode } from '../../core/trucode';
import { HcpcsGroup, HcpcsResult, Policy } from '../../core/trucode-domain';
import { codingDrawerWidth } from '../coding-tool/coding-route';
import { LinkButton } from '../common/presentation';

let selectedCode: string;

function IcdLinks({ range, api }) {
  const { start_code, end_code } = range;
  const [anchorEl, setAnchorEl] = useState<any>();

  const onClick =
    (code: string, type: 'dx' | 'cpt' = 'dx') =>
    (e?: SyntheticEvent) => {
      selectedCode = code;
      setAnchorEl(e?.target);
    };

  const onSelect = (type: string) => {
    if (!type) {
      setAnchorEl(null);
      return;
    }

    if (type === AddDx) {
      api.getState().addIcd(selectedCode);
    } else {
      truCode.getState().openCodeBooks({
        filter: type === 'dx' ? 'ICD10CM_DX' : 'CPT4',
        query: selectedCode,
        events: {
          onCodePosted: (e) => {
            if (e.codes && e.codes.length) {
              // TODO import api
              // findCode(e.codes[0]);
            }
          },
        },
      });
    }
    setAnchorEl(null);
  };

  return (
    <>
      <Grid
        item
        container
        direction="row"
        spacing={0}
        alignItems="flex-start"
        style={{ whiteSpace: 'nowrap', width: 120, flexShrink: 0 }}
      >
        <Grid item>
          <Typography variant="caption">
            <LinkButton label={start_code} onClick={onClick(start_code)} />
          </Typography>
        </Grid>
        <Grid
          item
          style={{
            color: orange,
            flexGrow: 1,
            textAlign: 'center',
            maxWidth: 12,
          }}
        >
          <Typography variant="caption">-</Typography>
        </Grid>
        <Grid item>
          <Typography variant="caption">
            <LinkButton label={end_code} onClick={onClick(end_code)} />
          </Typography>
        </Grid>
      </Grid>
      <DxMenu onSelect={onSelect} anchorEl={anchorEl} />
    </>
  );
}

function IcdGroup({ group, api }) {
  const { coding_system, codes } = group;
  const list = codes || [];
  return (
    <Expansion defaultExpanded={true} style={{ width: '100%', height: '100%' }}>
      <ExpansionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="caption">{coding_system} Groups</Typography>
      </ExpansionSummary>
      <ExpansionDetails>
        <Grid
          container
          justify="flex-start"
          alignContent="flex-start"
          spacing={2}
          style={{ width: '100%' }}
        >
          {list.map((v, i) => (
            <IcdLinks key={i} range={v} api={api} />
          ))}
        </Grid>
      </ExpansionDetails>
    </Expansion>
  );
}

const editSearch = create<{ result: HcpcsResult; loading: boolean }>((set) => ({
  result: null,
  loading: false,
}));

let pendingSubmit = false;
const editSearchForm = formApi({
  values: {
    dx: null,
    cpt: null,
  },
  fields: {
    dx: {
      required: true,
      label: 'Diagnosis',
      onEnter: () => editSearchForm.submit(),
      clearIcon: false,
    },
    cpt: {
      required: true,
      label: 'Procedure',
      onEnter: () => editSearchForm.submit(),
      clearIcon: false,
    },
  },
  valid: (v) => v.dx?.length && v.cpt?.length,
  onSubmit: async (v) => {
    if (pendingSubmit) {
      return;
    }
    editSearch.setState({ result: null, loading: true });
    try {
      pendingSubmit = true;
      const result = await medicalEditsRequest(v);
      if (result?.hcpcs_procedures) {
        const g: HcpcsGroup = result?.hcpcs_procedures[0];

        if (g && g.policies) {
          const p: Policy = g?.policies[0];
          if (p?.article_id) {
            // TODO resolve article id / copyright
            //const article = await getArticleRequest({id: p?.article_id});
            // console.log('article', p)
          }
        }
      }
      pendingSubmit = false;
      editSearch.setState({ result, loading: false });
    } catch (error) {
      console.log(error);
      editSearch.setState({ result: null, loading: false });
    }
  },
});

export function searchEdits(dx: string, cpt: string) {
  editSearchForm.setValues({ dx, cpt });
  editSearchForm.submit();
}

type EditSearchProps = {
  api?: IcdCptApi;
};

let initialized: boolean;
export function EditSearch(props: EditSearchProps) {
  const { dx, cpt } = editSearchForm.controls;
  const result = editSearch((s) => s.result);
  const loading = editSearch((s) => s.loading);

  const n = result?.hcpcs_procedures?.length;
  const a: Alert = truCode((s) => s.alert);

  useEffect(() => {
    // console.log('EditSearch alert', a, pendingSubmit);
    if (a && !pendingSubmit) {
      //TODO map alerts, setAlertTransients
      if (a.diagnoses?.length) {
        searchEdits(a.diagnoses.join(','), a.cptCode);
      }
    }

    if (!initialized) {
      // console.log('edits init', )
      searchEdits('', '');
      initialized = true;
    }
  }, [a]);

  return (
    <div
      style={{
        maxWidth: codingDrawerWidth,
        height: '100%',
        paddingTop: 12,
        paddingLeft: 24,
        paddingRight: 24,
      }}
    >
      <Typography variant="subtitle1">
        Search Medical Necessity Edits
      </Typography>

      <Grid
        item
        container
        direction="row"
        alignItems="center"
        justify="flex-start"
        spacing={2}
        style={{ maxWidth: codingDrawerWidth }}
      >
        <Grid item xs={5}>
          <Text {...dx} />
        </Grid>
        <Grid item xs={5}>
          <Text {...cpt} />
        </Grid>
        <Grid item xs={1}>
          <IconButton onClick={() => editSearchForm.submit()}>
            <Search />
          </IconButton>
        </Grid>
      </Grid>
      <br />
      {n > 0 && (
        <div style={{ width: '100%', height: '74vh', overflowY: 'auto' }}></div>
      )}

      {!n && result !== null && (
        <div style={{ paddingTop: 48, marginLeft: '35%' }}>
          {' '}
          <Typography variant="overline">No Results</Typography>
        </div>
      )}
      {loading && (
        <div style={{ paddingTop: 36, marginLeft: '36%' }}>
          <CircularProgress />
        </div>
      )}
    </div>
  );
}

const SearchDx = 'Search Tru Code';
const AddDx = 'Add Diagnosis';

type DxMenuProps = {
  anchorEl: any;
  onSelect: (choice: string) => void;
};
export default function DxMenu(props: DxMenuProps) {
  const { onSelect, anchorEl } = props;
  const onClose = (type: string) => (e: any) => {
    //console.log('DxMenu', type, e)
    onSelect(type);
  };

  return (
    <Menu
      keepMounted
      anchorEl={anchorEl}
      open={anchorEl}
      onClose={onClose(null)}
    >
      <MenuItem onClick={onClose(SearchDx)}>{SearchDx}</MenuItem>
      <MenuItem onClick={onClose(AddDx)}>{AddDx}</MenuItem>
    </Menu>
  );
}

type EditApi = {
  alert: Alert;
  addIcd: (code: string) => void;
  searchCode: (selectedCode: string, type?: string) => void;
};

export type EditStore = UseStore<EditApi>;

export function createEditApi(props: { icdCpt: IcdCptApi }): EditStore {
  const { icdCpt } = props;
  return create<EditApi>((set) => ({
    alert: null,
    addIcd: (code: string) => {
      if (icdCpt) {
        icdCpt.findCode({ code });
      }
      set({ alert: null });
    },
    searchCode: (selectedCode: string, type = 'dx') => {
      // console.log(selectedCode);
      truCode.getState().openCodeBooks({
        filter: type === 'dx' ? 'ICD10CM_DX' : 'CPT4',
        query: selectedCode,
        events: {
          onCodePosted: (e) => {
            if (e.codes && e.codes.length) {
              icdCpt.findCode(e.codes[0]);
              set({ alert: null });
            }
          },
        },
      });
    },
  }));
}

type EditProps = {
  api: EditStore;
  description?: string;
  cptCode?: string;
};

function DescriptionLinks(props: EditProps) {
  const { description, api, cptCode } = props;

  const onClick = (code: string) => () => {
    api.getState().searchCode(code, code === cptCode ? 'cpt' : 'dx');
  };

  function Segment(token: String) {
    if (!token?.length) {
      return token;
    }
    const i = token.lastIndexOf(' ');
    const text = token.substring(0, i);

    const code = token
      .substring(i + 1)
      .replace('(', '')
      .replace(')', '');

    const validCode = code.length > 2;
    return (
      <>
        {' ' + text + ' '}
        {validCode && <LinkButton label={code} onClick={onClick(code)} />}
      </>
    );
  }

  const segments = description.split('\n');
  let last = null;

  // console.log(description.charAt(description.length - 1) === '\n', description.charAt(description.length - 1))
  const i = segments.length - 1;
  if (segments[i].charAt(segments[i].length - 1) === ':') {
    last = segments.pop();
  }

  return (
    description && (
      <>
        {segments.map((token) => Segment(token))}
        {last}
      </>
    )
  );
}

export function EditDialog(props: EditProps) {
  const { api } = props;
  const alert = api((s) => s.alert);
  const onClose = () => {
    api.setState({ alert: null });
  };

  const { details, notes, errorText, description, cptCode } = alert || {};
  const { macName, sourceData } = details || {};

  //TODO:  should we display 'notes' ?
  // console.log('EditDialog', alert)

  return (
    alert &&
    alert?.details && (
      <Dialog open={!!alert} maxWidth={false}>
        <DialogTitle>Tru Code Alert</DialogTitle>
        <DialogContent
          style={{
            width: 420,
            paddingTop: 0,
            zIndex: 1501,
          }}
        >
          {<DescriptionLinks {...{ ...props, ...{ description, cptCode } }} />}
          {errorText && (
            <Typography variant="body2">
              <br />
              {errorText}
            </Typography>
          )}
          {macName && (
            <Typography variant="button">
              <br />
              {macName}
            </Typography>
          )}
          {sourceData && (
            <Typography variant="button">
              <br />
              {sourceData}
            </Typography>
          )}
          {notes && (
            <Typography variant="body2">
              <br />
              {notes}
            </Typography>
          )}
          {(details?.diagnosisGroups || []).map((v, i) => (
            <IcdGroup key={i} group={v} api={api} />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary" variant="outlined">
            {' '}
            Close{' '}
          </Button>
        </DialogActions>
      </Dialog>
    )
  );
}
